/**
 * Structured sample transcripts for testing templates in the builder
 * preview. Add new entries to SAMPLE_TRANSCRIPTS — the sample picker
 * renders whatever is in this list, so this is the single place to
 * dump examples (aim for breadth across specialties AND visit types:
 * preventive, acute, chronic follow-up, new consults, med management,
 * procedures, telehealth, …).
 *
 * Transcript format matches SampleTranscriptsBySpecialty in
 * @eluve/utils: alternating clinician/patient lines, one utterance per
 * line, no speaker labels.
 */

export type SampleTranscript = {
  id: string;
  /** Shown in the sample picker, e.g. "Family Medicine — Annual Physical" */
  label: string;
  specialty: string;
  visitType: string;
  transcript: string;
};

const familyMedicineAnnualPhysical = `Good morning, Maria. Good to see you again. It's been about a year — anything on your mind before we get started?
Mostly here for the checkup, but I have been more tired than usual the last couple of months.
Okay, we'll dig into that. Any changes in your health since last year? New diagnoses, hospital visits, new medications?
No hospital visits. I'm still just taking the levothyroxine, 75 micrograms.
Taking it every morning on an empty stomach?
Most days. Sometimes I forget and take it with breakfast.
That can affect absorption, so let's check your thyroid levels today — that could also explain the tiredness. How's your sleep?
Honestly, not great. I'm in bed by eleven but I scroll on my phone, and I'm up at six for the kids.
So maybe six hours on a good night. Any snoring or waking up gasping?
My husband says I snore sometimes, but nothing dramatic.
Noted. How about exercise?
I walk the dog twenty minutes most days. Nothing else really.
That's a good base. Diet — coffee, alcohol?
Two coffees a day. Wine maybe twice a week, one glass.
Reasonable. Any smoking or vaping?
No, never.
Your blood pressure today is 118 over 76, weight is stable from last year, BMI 26. You're due for a few screenings: you turned 45 in March, so I'd like to set up your first colon cancer screening. We can do a colonoscopy or start with a stool-based test.
Is the stool test easier?
It's done at home, but if it's positive you'd need the colonoscopy anyway. Either is a fine choice.
Let's try the stool test first.
Done. You're also due for a tetanus booster — your last one was 2014. We can do that today.
Sure, that's fine.
For the fatigue, I'm ordering a CBC, a metabolic panel, TSH, ferritin, and vitamin D along with the cholesterol panel. If everything comes back normal, the next suspects are sleep and stress, and we'll talk about sleep habits.
That makes sense. My mom was low on iron for years, so I've wondered about that.
Good to know — ferritin will cover that. Anything else today? Mood okay, no changes you're worried about?
Mood is fine. Busy, but fine.
All right. Lab slip is in the system, fast for eight hours before the draw. The nurse will give you the tetanus shot before you leave, and the stool kit will be mailed to you. I'll message you results within a week.
Thank you, doctor.`;

const pediatricsSickVisit = `Hi there. And who do we have today — this must be Noah. What's going on, Mom?
He's been pulling at his right ear since Sunday, and last night he had a fever of 102.3.
Poor guy. Noah, does your ear hurt right now? Can you point to where?
This one.
The right one, okay buddy. Any discharge or fluid coming out of the ear that you noticed?
No, nothing like that.
How's he been eating and drinking?
Less than usual, but he had a popsicle and some juice this morning. He's been cranky and didn't sleep much.
Any vomiting, diarrhea, or rash?
No.
Did he have a cold recently?
Yes, a runny nose for about a week before this started.
That's the usual story — ear infections often follow colds at this age. Any history of ear infections before?
One last winter, around ten months ago. He got amoxicillin and it cleared up.
Any allergies to medications?
No allergies.
Okay, Noah, I'm going to look in your ears with my little flashlight. You'll feel a tiny tickle. Here we go — left ear looks great. Now the right one. There it is: the right eardrum is red and bulging, with fluid behind it. That's an acute middle ear infection.
Does he need antibiotics again?
At his age, with a fever over 102 and clear findings, yes, I'd treat. Since amoxicillin worked before, we'll use it again — high dose, twice a day for ten days. The chewable or the liquid?
Liquid is easier for him.
Liquid it is. For the pain and fever, alternate ibuprofen and acetaminophen as needed — I'll write the doses by his weight, he's 16 kilos today. The fever should break within 48 to 72 hours of starting the antibiotic.
What if it doesn't?
If he's still febrile after 72 hours, or he gets worse, comes back in. Also come back right away if you see swelling behind the ear, a stiff neck, or he becomes very drowsy.
Okay. Can he go back to daycare?
Once he's fever-free for 24 hours and feeling up to it. One more thing — he's due for his flu shot. Want to do that today, or wait until he's better?
Let's wait until he's better.
Completely reasonable. Book a nurse visit in a couple of weeks. Feel better, Noah — you did great today.`;

const cardiologyFollowUp = `Mr. Okafor, good to see you. It's been three months since we started the new heart failure regimen. How are you feeling?
Honestly, better. I can get up the stairs at home without stopping now.
That's exactly what we want to hear. Before, you were stopping halfway. Any chest pain, palpitations, or blackouts?
No chest pain. Once in a while I feel a flutter for a second or two, but it passes.
How often?
Maybe twice a week. It doesn't bother me much.
We'll keep an eye on it — your last monitor showed only occasional extra beats, nothing dangerous. How about swelling in your ankles?
Almost gone. I only notice a little at night if I've been on my feet all day.
And your weight at home?
Steady, 86 to 87 kilos every morning. I write it down like you said.
Excellent habit. Any dizziness when standing up?
A little, the first week after we raised the medication, but it's gone now.
Good. You're on sacubitril-valsartan 97/103 twice daily, carvedilol 25 twice daily, dapagliflozin 10 daily, and furosemide 40 in the morning. Taking everything as prescribed?
Yes. The pharmacy synced them so I pick everything up at once.
Your blood pressure today is 112 over 70, heart rate 64, both right where I want them. Labs from last week: kidney function stable, potassium 4.4, and your BNP has come down from 1,840 to 520.
Is that good?
Very good — it's a marker of strain on the heart, and it's down more than 70 percent. Your echo last month showed your ejection fraction improved from 30 to 40 percent.
So the heart is getting stronger?
It's responding to the therapy, yes. The job now is to protect those gains. I want to nudge the furosemide down to 20 milligrams since the congestion has cleared — keep weighing yourself daily, and if you gain more than two kilos in three days or the ankle swelling returns, call us and we'll bump it back up.
Okay, I can do that.
Salt still under two grams a day?
My wife runs a strict kitchen now.
That's half the battle. Have you thought about the cardiac rehab referral?
I'd like to try it now that I can move better.
I'll send the referral today. Repeat labs in six weeks to check kidneys and potassium after the diuretic change, and I'll see you in three months with another echo before that visit.
Thank you, doctor. This is the best I've felt in two years.`;

const orthopedicsNewInjuryConsult = `Hi, I'm Dr. Lindqvist. So I hear the knee gave you some trouble on the soccer field — walk me through what happened.
We were playing Sunday league. I planted my right foot to cut left, felt a pop in my knee, and went down. Couldn't finish the game.
Did the knee swell up?
Within a couple of hours it was like a balloon. That was twelve days ago. The swelling has come down some but it's still puffy.
Have you been able to bear weight?
I can walk, but it doesn't feel trustworthy. Twice it's buckled on me — once on stairs.
That giving-way feeling is important. Any locking, where the knee gets stuck and you can't straighten it?
No locking, just instability.
Any previous injuries to either knee?
Sprained the same knee skiing maybe five years ago, but that healed in a couple of weeks.
Okay, let's take a look. Lie back for me. There's still a moderate effusion. Bend both knees — flexion is limited about ten degrees on the right compared to the left. Now relax and let me move it. Lachman test — there's noticeably more forward shift on the right with a soft endpoint. Pivot shift is hard to assess because you're guarding, which is normal. Joint line tenderness on the inner side as well. McMurray's gives a click but no clear pain.
What does all that mean?
The exam points strongly toward a torn ACL — the ligament that controls that pivoting motion — and possibly a medial meniscus tear alongside it, which is a common pair with this mechanism. The X-rays we took today show no fracture.
So what's next, an MRI?
Exactly. The MRI confirms the diagnosis and maps out the meniscus, which changes the surgical plan. I'll order it this week.
Will I definitely need surgery?
Not definitely — it depends on the MRI and on you. You're 29 and you want to keep playing cutting sports, which usually favors reconstruction. If you were happy with cycling and jogging, rehab alone can be reasonable. We'll decide together once we have the imaging.
What do I do in the meantime?
Three things. First, physical therapy starting this week — prehab matters; quads strength and getting full extension back improve surgical outcomes. Second, the compression sleeve and ice for the swelling. Third, no pivoting sports — straight-line stationary bike is fine once it's comfortable.
Can I still go to work? I'm on my feet a lot.
Yes, with the brace I'll give you today for the giving-way episodes. Use the stairs carefully.
And if it is surgery, how long am I out?
Return to pivoting sports is typically nine to twelve months with a graft. We'll talk through graft choices if we get there. Let's get the MRI and meet again in two weeks with the pictures.
Sounds like a plan. Thanks, doc.`;

const psychiatryMedFollowUp = `Hi Dana, come on in. It's been six weeks since we increased the sertraline to 100. How have things been?
Better in some ways. The constant dread in the mornings has mostly lifted.
That's a meaningful change. On the zero-to-ten scale we've been using, where's the anxiety day to day?
Maybe a four. It was a seven or eight when I first came in.
And the low mood?
Also better. I'm cooking again, and I saw friends twice last week, which I hadn't done in months.
I'm glad to hear it. Your PHQ-9 today is 8, down from 17, and the GAD-7 is 9, down from 15 — that matches what you're describing. Any side effects? Stomach upset, headaches, anything with sleep?
The nausea from the first weeks is gone. But I've noticed I wake up around 4 a.m. a couple of times a week and can't fall back asleep.
Is that new since the dose increase?
I think it was happening before, but it's more noticeable now that the rest is better.
Okay. Caffeine after noon?
…Sometimes a 3 p.m. coffee.
Let's start there — cut it to before noon and we'll see if the 4 a.m. waking improves before we change anything medication-wise. Any alcohol?
Rarely. Maybe one drink a month.
Fine. I have to ask the standard questions: any thoughts of harming yourself or that life isn't worth living?
No. Even at my worst it wasn't that, and definitely not now.
Good. And the panic episodes — any since we last met?
One, at the airport last month. But I used the breathing thing my therapist taught me and it passed without me leaving the security line. Last year I would have gone home.
That's a genuine win — staying in the situation is exactly how the brain unlearns the alarm. You're still seeing Rachel weekly?
Every Tuesday. We've started the exposure list.
Excellent — the combination is what's moving this. Here's my thinking: the response to 100 milligrams is good but six weeks is still early plateau, so I wouldn't increase today. Let's hold the dose, fix the afternoon caffeine, and reassess sleep in four weeks. If the early waking persists, we have options then.
That works. How long will I be on this in total?
Standard advice for a first clear episode is to continue at least six to twelve months after you're feeling fully well, then taper slowly if you want to. We're not making that decision today.
Okay. That's less scary than I thought.
Anything else on your mind?
No, this was good. It helps to see the numbers going down.
Same time, four weeks from now. And message the portal if the sleep gets worse rather than waiting.`;

const physicalTherapyFollowUp = `Morning, Priya. Session six for the right shoulder — how did it respond after last week?
Much better week. The day-to-day ache is down to about a two out of ten. Reaching overhead still pinches, maybe a five when I put dishes away on the top shelf.
Any night pain? That was waking you early on.
Not for two weeks now. I sleep through.
Good marker. How did the home program go — the band work and the wall slides?
I did the bands five days, wall slides most days. The new one, the sideways raise with the thumb up, I felt that one a lot.
Feeling it in the muscle on the outside of the shoulder is fine; sharp pain in the front is not. Which was it?
Muscle burn, not the pinch.
Perfect. Let's measure. Arm up for me — flexion is 165 today, you were 150 two weeks ago. Out to the side, abduction 150, up from 135. Rotation behind the back reaches your belt line now.
That's the first time I could do that without wincing.
Strength next. Push out against my hand — external rotation is grade four plus, and the empty-can position is four, up from four minus. Still a little catch with the impingement test but your drop-arm is solid.
So it's healing?
The tendon is tolerating load better, which is the goal. Today we'll progress you: heavier band for external rotation, start eccentric lowering with a two kilo dumbbell, and add the kneeling press progression.
Okay. The eccentric one is the slow lowering thing?
Exactly — three seconds down, five reps, three sets, every other day. Keep the wall slides daily as a warm-up.
Can I get back to tennis soon? Doubles starts next month.
Let's aim for gentle ground strokes in two weeks if the overhead pinch keeps fading, serves a month after that. Overhead is always last.
That works with the season.
Anything else bothering you? Neck okay with the new desk setup?
Neck's been fine since we raised the monitor.
Great. Same time next week — we'll re-test the empty-can and decide on the tennis progression then.`;

const dermatologyLesionConsult = `Hi Mr. Tan, I'm Dr. Osei. Your GP sent you over about a mole on your back — tell me what you've noticed.
My wife spotted it in January. She says it's gotten bigger since, and the color looks darker on one side now.
So roughly six months of change. Any itching, bleeding, or crusting?
It itched a couple of times last month. No bleeding.
Any other spots changing that you know of?
Not that we've seen.
Tell me about sun exposure over the years.
A lot, honestly. I grew up on the coast, surfed through my twenties, plenty of burns. A few bad blistering ones as a teenager.
Do you use sunscreen now?
Only at the beach. Not day to day.
Any family history of skin cancer?
My father had a melanoma removed from his shoulder in his sixties.
That's important context. Any immune conditions or medications like immunosuppressants?
No, I'm healthy. Just blood pressure medication, amlodipine.
Let's have a look. I'm examining the lesion on the left upper back — it's seven millimeters, asymmetric, the border is irregular on the lower edge, and there are two tones of brown with a darker focus at six o'clock. Under the dermatoscope I see an atypical network and some peripheral streaks at the darker edge.
Is it cancer?
It has features that concern me — the asymmetry, the recent change, and the dermoscopy pattern. It needs to come off and be tested. That's the only way to know for certain.
You can do that here?
Yes, an excisional biopsy under local anesthetic, the whole lesion with a two millimeter margin, a few stitches, fifteen minutes. We can do it today.
Okay, let's just do it today then.
While you're here I'll do a full skin check as well. … The rest of the exam shows sun damage and scattered ordinary moles, two flat brown spots on the shoulders I'm photographing for the file, nothing else suspicious today.
What happens after the biopsy?
Pathology takes about a week. I'll call you with the result either way. If it comes back as melanoma we'll discuss next steps then — don't get ahead of it tonight.
And the stitches?
Out in two weeks with our nurse. Keep it dry for forty-eight hours. From now on: daily SPF 50 on exposed skin, hats outdoors, and a skin self-check monthly — photograph anything you're unsure about. With your history I want to see you yearly regardless of this result.`;

const psychologyTherapySession = `Come in, Jonas. Where would you like to start today?
The work presentation happened. The one we built the exposure plan around.
You did it? Walk me through it.
Tuesday morning, eight people in the room. Heart was pounding beforehand, I'd say anxiety hit an eight while I was waiting to start.
And once you started speaking?
The first two minutes were rough — I noticed my hands shaking and had the thought, everyone can see I'm falling apart. But I used the grounding thing, feet on the floor, slowed my breathing, and by the middle it dropped to maybe a four.
You stayed in it the whole way through?
I did. Didn't hand it off to Marcus like last quarter.
That's the biggest exposure you've completed since we started. What did you make of the shaking-hands thought afterwards?
Afterwards I asked Marcus if he'd noticed anything. He said no, he just thought it was a normal presentation. So the catastrophe was mostly in my head.
That's the evidence-gathering we practiced — you ran the experiment instead of taking the thought at face value. Did anything go worse than expected?
One question I couldn't answer. Old me would have spiraled about it for days. I just said I'd follow up by email, and I did.
How has the rest of the week been — sleep, the Sunday-night dread?
Sleep is okay, six and a half hours most nights. Sunday night I was anxious about the week, maybe a five, but I didn't cancel Monday's gym session like I used to.
Any avoidance you noticed slipping back in?
I still took the stairs instead of sharing the lift with the directors on Thursday. I knew I was doing it, too.
Good catch — noticing in the moment is half the work. Let's put lift-with-directors on the hierarchy; where would you rate it?
Honestly only a five now. The presentation was the mountain.
Then it's a natural next step. This week: ride the lift at least three times when others are in it, log the predicted anxiety and the actual, same worksheet. And one self-disclosure exercise — mention to a colleague that you get nervous presenting.
Telling someone on purpose? That one feels worse than the lift.
That's why it's powerful — it tests whether the anxiety itself is the shameful secret your mind says it is. We can role-play it now if you want.
Yeah, let's practice it. I'd rather stumble here first.`;

const medSpaProcedureVisit = `Welcome back, Sofia. Last time we talked about treating the frown lines — still what you'd like to do today?
Yes, the eleven lines between my brows. They make me look angry on video calls even when I'm relaxed.
Let's confirm a few things first. Any changes in your health since the consult — pregnancy, breastfeeding, new medications?
No changes. Not pregnant, no new meds.
Any neuromuscular conditions in you or the family — myasthenia gravis, ALS?
No.
Have you had neurotoxin before?
Once, about two years ago at another clinic. It worked well, no problems. I think it was twenty units.
Any blood thinners, aspirin, fish oil in the last week?
No. I stopped my fish oil like the booking email said.
Perfect. Frown for me… and relax. Strong corrugator activity, moderate static line on the left even at rest. Raise your brows… good, frontalis is symmetric. I'd recommend twenty units across five injection points in the glabella today. We'll leave the forehead alone this round so you keep full brow movement.
That's what I want — I don't want the frozen look.
Understood, this dosing keeps natural movement. The main risks to know: bruising at the injection sites, a heavy-brow feeling for a few days, and rarely a temporary eyelid droop, which resolves on its own. Results start around day three and settle by day fourteen, lasting three to four months.
Okay, I'm comfortable with that.
I have your signed consent from check-in and baseline photos from the consult — I'll take today's set now. … Done. Skin cleansed, ice for comfort. Small pinches now — five injection points, four units each, twenty units total of onabotulinumtoxinA, fresh vial reconstituted this morning.
That was quicker than I remembered.
All done. Aftercare: stay upright for four hours, no rubbing or massaging the area today, skip the gym until tomorrow, makeup is fine after a few hours. Tiny bumps you see now fade within thirty minutes.
When will I see the full effect?
Full effect at two weeks. I'd like you back at the two-week mark for a quick review and touch-up if any movement remains — it's included.
Can we book the next round too?
Let's review at two weeks first, then we'll book you around the three-and-a-half month mark. Any heaviness or droop in the meantime, message us through the portal.`;

/**
 * The sample library shown in the template preview's sample picker.
 * Append new entries here.
 */
export const SAMPLE_TRANSCRIPTS: SampleTranscript[] = [
  {
    id: 'family-medicine-annual-physical',
    label: 'Family Medicine — Annual Physical',
    specialty: 'Family Medicine',
    visitType: 'Preventive / wellness exam',
    transcript: familyMedicineAnnualPhysical,
  },
  {
    id: 'pediatrics-sick-visit',
    label: 'Pediatrics — Sick Visit (Ear Pain)',
    specialty: 'Pediatrics',
    visitType: 'Acute illness visit',
    transcript: pediatricsSickVisit,
  },
  {
    id: 'cardiology-follow-up',
    label: 'Cardiology — Heart Failure Follow-up',
    specialty: 'Cardiology',
    visitType: 'Chronic disease follow-up',
    transcript: cardiologyFollowUp,
  },
  {
    id: 'orthopedics-knee-injury',
    label: 'Orthopedics — New Knee Injury Consult',
    specialty: 'Orthopedics',
    visitType: 'New patient consult',
    transcript: orthopedicsNewInjuryConsult,
  },
  {
    id: 'psychiatry-med-follow-up',
    label: 'Psychiatry — Medication Follow-up',
    specialty: 'Psychiatry',
    visitType: 'Medication management',
    transcript: psychiatryMedFollowUp,
  },
  {
    id: 'physical-therapy-follow-up',
    label: 'Physical Therapy — Shoulder Rehab Follow-up',
    specialty: 'Physical Therapy',
    visitType: 'Therapy follow-up',
    transcript: physicalTherapyFollowUp,
  },
  {
    id: 'dermatology-lesion-consult',
    label: 'Dermatology — Changing Mole Consult',
    specialty: 'Dermatology',
    visitType: 'New consult + procedure',
    transcript: dermatologyLesionConsult,
  },
  {
    id: 'psychology-therapy-session',
    label: 'Psychology — CBT Therapy Session',
    specialty: 'Psychology',
    visitType: 'Therapy session',
    transcript: psychologyTherapySession,
  },
  {
    id: 'med-spa-procedure',
    label: 'Med Spa — Neurotoxin Procedure Visit',
    specialty: 'Medical aesthetics',
    visitType: 'Procedure visit',
    transcript: medSpaProcedureVisit,
  },
];
